import React, { useCallback, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import moment from "moment";
import "react-datepicker/dist/react-datepicker.css";

import { Alert, Button, Card, Col, Row, Select, Table, Tabs, Typography } from "antd";
import { Line } from "@ant-design/charts";

import useDashboardProtoData from "./useDashboardProtoData";
import ExportCSV from "../../Components/ExportCSV";

const { Title, Text } = Typography;

function buildLineConfig(data, seriesField) {
  return {
    data: data || [],
    xField: "date",
    yField: "value",
    seriesField,
    xAxis: { type: "cat" },
    legend: { position: "bottom" },
    smooth: false,
  };
}

function buildGroupTableColumns(groupLabel, valueLabel) {
  return [
    { title: "Data", dataIndex: "date", key: "date" },
    { title: groupLabel || "Grupo", dataIndex: "group", key: "group" },
    { title: valueLabel || "Valor", dataIndex: "value", key: "value" },
  ];
}

function buildGroupCsvHeaders(groupLabel, valueLabel) {
  return [
    { label: "Data", key: "date" },
    { label: groupLabel || "Grupo", key: "group" },
    { label: valueLabel || "Valor", key: "value" },
  ];
}

export default function DashboardProto() {
  const [filterType, setFilterType] = useState("payment_method");
  const [hasLoaded, setHasLoaded] = useState(false);

  const { mode, viewModel, raw, isLoading, error, load } = useDashboardProtoData({
    mode: "api",
    noAuth: true,
    endpoint: "https://swdhuvwq3sabyelhlf7zx5egxq0wscnw.lambda-url.us-east-1.on.aws/",
  });
  const [startDate, setStartDate] = useState(() => new Date());
  const [endDate, setEndDate] = useState(() => new Date());

  const kpiItems = useMemo(() => {
    if (!hasLoaded) {
      return [
        { key: "faturamento", label: "Faturamento do período", value: 0, format: "brl" },
        { key: "meta", label: "Meta do período", value: 0, format: "brl" },
        { key: "mrr", label: "MRR do período", value: 0, format: "brl" },
        { key: "arr", label: "ARR do período", value: 0, format: "brl" },
      ];
    }

    const cards = viewModel?.cards || [];
    const findValue = (label) => {
      for (const card of cards) {
        const values = card?.values || {};
        if (values[label] !== undefined && values[label] !== null) return values[label];
      }
      return undefined;
    };

    const items = [
      {
        key: "faturamento",
        label: "Faturamento do período",
        value: findValue("Faturamento (R$)"),
        format: "brl",
      },
      { key: "meta", label: "Meta do período", value: findValue("Meta (R$)"), format: "brl" },
    ];

    items.push({ key: "mrr", label: "MRR do período", value: findValue("MRR (R$)"), format: "brl" });
    items.push({ key: "arr", label: "ARR do período", value: findValue("ARR (R$)"), format: "brl" });

    return items.filter((i) => i.value !== undefined && i.value !== null);
  }, [hasLoaded, viewModel?.cards]);

  const formatKpiValue = useCallback((value, format) => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "string") return value;
    if (typeof value === "number") {
      if (format === "brl") {
        return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);
      }
      return new Intl.NumberFormat("pt-BR").format(value);
    }
    return String(value);
  }, []);

  const dateStartStr = useMemo(() => (startDate ? moment(startDate).format("YYYY-MM-DD") : ""), [startDate]);
  const dateEndStr = useMemo(() => (endDate ? moment(endDate).format("YYYY-MM-DD") : ""), [endDate]);

  const onLoad = useCallback(() => {
    setHasLoaded(true);
    load({ dateStart: dateStartStr, dateEnd: dateEndStr, filterType });
  }, [dateEndStr, dateStartStr, filterType, load]);

  return (
    <div style={{ padding: 16 }}>
      <style>{`
        .dashboard-proto-tabs .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
          color: #0A4148;
        }
        .dashboard-proto-tabs .ant-tabs-ink-bar {
          background: #0A4148;
        }
      `}</style>
      <Row gutter={[16, 16]} align="middle">
        <Col flex="auto">
          <Title level={3} style={{ margin: 0 }}>
            Dashboard (Protótipo)
          </Title>
          <Text type="secondary">
            Modo: <b>{mode}</b>
          </Text>
        </Col>
        <Col>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
            <Select
              value={filterType}
              onChange={(v) => setFilterType(v)}
              style={{ width: 220 }}
              options={[
                { value: "payment_method", label: "Tipo pagamento" },
                { value: "name", label: "Tipo plano" },
                { value: "classificacao_cliente", label: "Tipo usuário" },
              ]}
            />
            <DatePicker
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              placeholderText="Data inicial"
              dateFormat="dd/MM/yyyy"
            />
            <DatePicker
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              placeholderText="Data final"
              dateFormat="dd/MM/yyyy"
            />
            <Button type="primary" onClick={onLoad} loading={isLoading}>
              Carregar dados
            </Button>
          </div>
        </Col>
      </Row>

      {error ? (
        <div style={{ marginTop: 16 }}>
          <Alert type="error" message="Erro" description={String(error)} />
        </div>
      ) : null}

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        {kpiItems.map((item) => (
          <Col key={item.key} xs={24} sm={12} md={6}>
            <Card loading={isLoading} style={{ height: "100%" }}>
              <Text style={{ display: "block" }}>{item.label}</Text>
              <div style={{ fontSize: 28, fontWeight: 600, lineHeight: "34px", marginTop: 6 }}>
                {formatKpiValue(item.value, item.format)}
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={24}>
          <Tabs
            className="dashboard-proto-tabs"
            defaultActiveKey="transactions"
            items={[
                {
                  key: "transactions",
                  label: "Transações",
                  children: (
                    <div>
                      <Row gutter={[16, 16]}>
                        <Col span={24}>
                          <Card
                            title={viewModel?.charts?.transactions?.sales?.title || "Número de Vendas"}
                            size="small"
                          >
                            <Line
                              {...buildLineConfig(
                                viewModel?.charts?.transactions?.sales?.data,
                                "group",
                              )}
                            />
                            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
                              <ExportCSV
                                name="Download CSV"
                                className="ant-btn ant-btn-default"
                                filename={`transacoes_vendas_${dateStartStr}_${dateEndStr}.csv`}
                                data={viewModel?.charts?.transactions?.sales?.data || []}
                                headers={buildGroupCsvHeaders("Grupo", "Vendas")}
                              />
                            </div>
                            <Table
                              columns={buildGroupTableColumns("Grupo", "Vendas")}
                              dataSource={(viewModel?.charts?.transactions?.sales?.data || []).map(
                                (r, idx) => ({ key: String(idx), ...r }),
                              )}
                              size="small"
                              pagination={{ pageSize: 8 }}
                              style={{ marginTop: 12 }}
                            />
                          </Card>
                        </Col>
                        <Col span={24}>
                          <Card
                            title={viewModel?.charts?.transactions?.ticket?.title || "Ticket Médio"}
                            size="small"
                          >
                            <Line
                              {...buildLineConfig(
                                viewModel?.charts?.transactions?.ticket?.data,
                                "group",
                              )}
                            />
                            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
                              <ExportCSV
                                name="Download CSV"
                                className="ant-btn ant-btn-default"
                                filename={`transacoes_ticket_${dateStartStr}_${dateEndStr}.csv`}
                                data={viewModel?.charts?.transactions?.ticket?.data || []}
                                headers={buildGroupCsvHeaders("Grupo", "Ticket")}
                              />
                            </div>
                            <Table
                              columns={buildGroupTableColumns("Grupo", "Ticket")}
                              dataSource={(viewModel?.charts?.transactions?.ticket?.data || []).map(
                                (r, idx) => ({ key: String(idx), ...r }),
                              )}
                              size="small"
                              pagination={{ pageSize: 8 }}
                              style={{ marginTop: 12 }}
                            />
                          </Card>
                        </Col>
                      </Row>
                    </div>
                  ),
                },
                {
                  key: "cancellations",
                  label: "Cancelamentos",
                  children: (
                    <div>
                      <Row gutter={[16, 16]}>
                        <Col span={24}>
                          <Card
                            title={
                              viewModel?.charts?.cancellations?.involuntary?.title ||
                              "Cancelamentos Involuntários"
                            }
                            size="small"
                          >
                            <Line
                              {...buildLineConfig(
                                viewModel?.charts?.cancellations?.involuntary?.data,
                                "group",
                              )}
                            />
                            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
                              <ExportCSV
                                name="Download CSV"
                                className="ant-btn ant-btn-default"
                                filename={`cancelamentos_involuntarios_${dateStartStr}_${dateEndStr}.csv`}
                                data={viewModel?.charts?.cancellations?.involuntary?.data || []}
                                headers={buildGroupCsvHeaders("Grupo", "Cancelamentos")}
                              />
                            </div>
                            <Table
                              columns={buildGroupTableColumns("Grupo", "Cancelamentos")}
                              dataSource={(viewModel?.charts?.cancellations?.involuntary?.data || []).map(
                                (r, idx) => ({ key: String(idx), ...r }),
                              )}
                              size="small"
                              pagination={{ pageSize: 8 }}
                              style={{ marginTop: 12 }}
                            />
                          </Card>
                        </Col>
                        <Col span={24}>
                          <Card
                            title={
                              viewModel?.charts?.cancellations?.voluntary?.title ||
                              "Cancelamentos Voluntários"
                            }
                            size="small"
                          >
                            <Line
                              {...buildLineConfig(
                                viewModel?.charts?.cancellations?.voluntary?.data,
                                "group",
                              )}
                            />
                            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
                              <ExportCSV
                                name="Download CSV"
                                className="ant-btn ant-btn-default"
                                filename={`cancelamentos_voluntarios_${dateStartStr}_${dateEndStr}.csv`}
                                data={viewModel?.charts?.cancellations?.voluntary?.data || []}
                                headers={buildGroupCsvHeaders("Grupo", "Cancelamentos")}
                              />
                            </div>
                            <Table
                              columns={buildGroupTableColumns("Grupo", "Cancelamentos")}
                              dataSource={(viewModel?.charts?.cancellations?.voluntary?.data || []).map(
                                (r, idx) => ({ key: String(idx), ...r }),
                              )}
                              size="small"
                              pagination={{ pageSize: 8 }}
                              style={{ marginTop: 12 }}
                            />
                          </Card>
                        </Col>
                      </Row>
                    </div>
                  ),
                },
                {
                  key: "users",
                  label: "Usuários",
                  children: (
                    <div>
                      <Row gutter={[16, 16]}>
                        <Col span={24}>
                          <Card
                            title={viewModel?.charts?.users?.users?.title || "Usuários"}
                            size="small"
                          >
                            <Line
                              {...buildLineConfig(viewModel?.charts?.users?.users?.data, "group")}
                            />
                            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
                              <ExportCSV
                                name="Download CSV"
                                className="ant-btn ant-btn-default"
                                filename={`usuarios_${dateStartStr}_${dateEndStr}.csv`}
                                data={viewModel?.charts?.users?.users?.data || []}
                                headers={buildGroupCsvHeaders("Grupo", "Usuários")}
                              />
                            </div>
                            <Table
                              columns={buildGroupTableColumns("Grupo", "Usuários")}
                              dataSource={(viewModel?.charts?.users?.users?.data || []).map((r, idx) => ({
                                key: String(idx),
                                ...r,
                              }))}
                              size="small"
                              pagination={{ pageSize: 8 }}
                              style={{ marginTop: 12 }}
                            />
                          </Card>
                        </Col>
                        <Col span={24}>
                          <Card
                            title={viewModel?.charts?.users?.signups?.title || "Novos Cadastros"}
                            size="small"
                          >
                            <Line
                              {...buildLineConfig(
                                viewModel?.charts?.users?.signups?.data,
                                "group",
                              )}
                            />
                            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
                              <ExportCSV
                                name="Download CSV"
                                className="ant-btn ant-btn-default"
                                filename={`novos_cadastros_${dateStartStr}_${dateEndStr}.csv`}
                                data={viewModel?.charts?.users?.signups?.data || []}
                                headers={buildGroupCsvHeaders("Grupo", "Cadastros")}
                              />
                            </div>
                            <Table
                              columns={buildGroupTableColumns("Grupo", "Cadastros")}
                              dataSource={(viewModel?.charts?.users?.signups?.data || []).map((r, idx) => ({
                                key: String(idx),
                                ...r,
                              }))}
                              size="small"
                              pagination={{ pageSize: 8 }}
                              style={{ marginTop: 12 }}
                            />
                          </Card>
                        </Col>
                      </Row>
                    </div>
                  ),
                },
              ]}
          />
        </Col>
      </Row>

    </div>
  );
}
